/**
 * @file 基础公式
 */

import { FormulateDataType } from './types'

const getBasic: () => FormulateDataType = () => ({
  key: 'basic',
  data: [
    {
      label: '二元运算符',
      items: [
        '+',
        '-',
        '\\times',
        '\\div',
        '\\pm',
        '\\mp',
        '\\triangleleft',
        '\\triangleright',
        '\\cdot',
        '\\setminus',
        '\\star',
        '\\ast',
        '\\cup',
        '\\cap',
        '\\sqcup',
        '\\sqcap',
        '\\vee',
        '\\wedge',
        '\\circ',
        '\\bullet',
        '\\oplus',
        '\\ominus',
        '\\odot',
        '\\oslash',
        '\\otimes',
        '\\bigcirc',
        '\\diamond',
        '\\uplus',
        '\\bigtriangleup',
        '\\bigtriangledown',
        '\\unlhd',
        '\\unrhd',
        '\\amalg',
        '\\wr',
        '\\dagger',
        '\\ddagger',
      ],
    },
    {
      label: '二元关系符',
      items: [
        '<',
        '>',
        '=',
        '\\le',
        '\\ge',
        '\\equiv',
        '\\ll',
        '\\gg',
        '\\doteq',
        '\\sim',
        '\\simeq',
        '\\approx',
        '\\subset',
        '\\supset',
        '\\subseteq',
        '\\supseteq',
        '\\sqsubset',
        '\\sqsupset',
        '\\sqsubseteq',
        '\\sqsupseteq',
        '\\cong',
        '\\Join',
        '\\propto',
        '\\in',
        '\\ni',
        '\\mid',
        '\\parallel',
        '\\perp',
        '\\notin',
        '\\ne',
      ],
    },
    {
      label: '箭头符号',
      items: [
        '\\gets',
        '\\to',
        '\\uparrow',
        '\\downarrow',
        '\\updownarrow',
        '\\leftrightarrow',
        '\\Uparrow',
        '\\Downarrow',
        '\\Updownarrow',
        '\\longleftrightarrow',
        '\\Leftarrow',
        '\\Rightarrow',
        '\\Leftrightarrow',
        '\\mapsto',
        '\\nearrow',
        '\\searrow',
        '\\swarrow',
        '\\nwarrow',
        '\\rightleftharpoons',
        '\\leftharpoonup',
        '\\rightharpoonup',
      ],
    },
    {
      label: '其他符号',
      items: [
        '\\because',
        '\\therefore',
        '\\dots',
        '\\forall',
        '\\exists',
        '\\nexists',
        '\\Finv',
        '\\neg',
        '\\infty',
        '\\Box',
        '\\Diamond',
        '\\bot',
        '\\top',
        '\\angle',
        '\\measuredangle',
        '\\sphericalangle',
        '\\surd',
        '\\heartsuit',
        '\\clubsuit',
        '\\spadesuit',
      ],
    },
  ],
})

const getGreek: () => FormulateDataType = () => ({
  key: 'greek',
  data: [
    {
      label: '小写',
      items: [
        '\\alpha',
        '\\beta',
        '\\gamma',
        '\\delta',
        '\\epsilon',
        '\\varepsilon',
        '\\zeta',
        '\\eta',
        '\\theta',
        '\\vartheta',
        '\\iota',
        '\\kappa',
        '\\lambda',
        '\\mu',
        '\\nu',
        '\\xi',
        'o',
        '\\pi',
        '\\varpi',
        '\\rho',
        '\\varrho',
        '\\sigma',
        '\\varsigma',
        '\\tau',
        '\\upsilon',
        '\\phi',
        '\\varphi',
        '\\chi',
        '\\psi',
        '\\omega',
      ],
    },
    {
      label: '大写',
      items: [
        '\\Gamma',
        '\\Delta',
        '\\Theta',
        '\\Lambda',
        '\\Xi',
        '\\Pi',
        '\\Sigma',
        '\\Upsilon',
        '\\Phi',
        '\\Psi',
        '\\Omega',
      ],
    },
    {
      label: '其他',
      items: [
        '\\imath',
        '\\imath',
        '\\jmath',
        '\\ell',
        '\\Re',
        '\\Im',
        '\\aleph',
        '\\beth',
        '\\gimel',
        '\\daleth',
        '\\wp',
        '\\mho',
        '\\backepsilon',
        '\\partial',
        '\\eth',
        '\\Bbbk',
        '\\complement',
        '\\circledS',
      ],
    },
  ],
})

const getDifferential: () => FormulateDataType = () => ({
  key: 'differential',
  data: [
    {
      label: '分数',
      items: [
        '\\frac{a}{b}',
        '\\mathrm{d}t',
        '\\frac{\\mathrm{d} y}{\\mathrm{d} x}',
        '\\partial t',
        '\\frac{\\partial y}{\\partial x}',
        '\\nabla\\psi',
        '\\frac{\\partial^2}{\\partial x_1\\partial x_2}y',
      ],
    },
    { label: '导数', items: ["{f}'", "{f}''", '{f}^{(n)}'] },
  ],
})

const getRadical: () => FormulateDataType = () => ({
  key: 'radical',
  data: [
    { label: '根式', items: ['\\sqrt{x}', '\\sqrt[n]{x}'] },
    {
      label: '上下标',
      items: ['x^{a}', 'x_{a}', 'x_{a}^{b}', 'x_{a}^{b}'],
    },
    {
      label: '重音符及其他',
      items: [
        '\\hat{a}',
        '\\check{a}',
        '\\grave{a}',
        '\\acute{a}',
        '\\tilde{a}',
        '\\breve{a}',
        '\\bar{a}',
        '\\vec{a}',
        '\\not{a}',
        '37^{\\circ}',
        '\\widetilde{ab}',
        '\\widehat{ab}',
        '\\overleftarrow{ab}',
        '\\overrightarrow{ab}',
        '\\overline{ab}',
        '\\underline{ab}',
        '\\stackrel\\frown{AB}',
        '\\overline{AB}',
        '\\overleftrightarrow{AB}',
        '\\overset{a}{\\leftarrow}',
        '\\overset{a}{\\rightarrow}',
        '\\xleftarrow[ab]{a}',
        '\\xrightarrow[ab]{a}',
      ],
    },
  ],
})

const getExtremity: () => FormulateDataType = () => ({
  key: 'extremity',
  data: [
    {
      label: '极限',
      items: [
        '\\lim  a',
        '\\lim_{x \\to 0}',
        '\\lim_{x \\to \\infty}',
        '\\textstyle \\lim_{x \\to 0}',
      ],
    },
    {
      label: '对数指数',
      items: ['\\log_{a}{b}', '\\lg_{a}{b}', '\\ln_{a}{b}', '\\exp a'],
    },
  ],
})

const getTrig: () => FormulateDataType = () => ({
  key: 'trig',
  data: [
    {
      label: '三角函数',
      items: ['\\sin a', '\\cos a', '\\tan a', '\\cot a', '\\sec a', '\\csc a'],
    },
    {
      label: '反三角函数',
      items: [
        '\\sin^{-1} a',
        '\\cos^{-1} a',
        '\\tan^{-1} a',
        '\\cot^{-1} a',
        '\\sec^{-1} a',
        '\\csc^{-1} a',
        '\\arcsin a',
        '\\arccos a',
        '\\arctan a',
        '\\operatorname{arccot} a',
        '\\operatorname{arcsec} a',
        '\\operatorname{arccos} a',
      ],
    },
  ],
})

/**
 * 获取基础公式数据
 *
 * @returns 基础公式数据
 */
export const getBasicData: () => FormulateDataType[] = () => [
  getBasic(),
  getGreek(),
  getDifferential(),
  getRadical(),
  getExtremity(),
  getTrig(),
]
